import { useCallback, useContext, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { BillingPlanCode, PaywallPlan } from '@nx-frontend/shared/types/paywall-types';
import { PaywallContext } from './paywall-context';
import { PaywallPlanColumn } from './paywall-plan-column';
import { $paywallStore, selectPlanEvent } from './store';
import { disablePlansByReason, mapToPlanCode } from './util';

export const PaywallPlans = () => {
  const { mode, isFreePlan, reason, isAppsumo, subscriptionType } = useContext(PaywallContext);
  const [paywallState] = useUnit([$paywallStore]);
  const [selectPlan] = useUnit([selectPlanEvent]);
  const t = useTranslations('features');

  const plansToShow = useMemo(() => {
    const plans = disablePlansByReason(paywallState.plans, reason);

    const currentPlanIndex = plans.findIndex(({ code }) => {
      const currentPlanCode = mapToPlanCode(paywallState.currentPlanCode);
      if (!currentPlanCode) {
        return code === mapToPlanCode((subscriptionType + '-yearly') as BillingPlanCode);
      }
      return code === currentPlanCode;
    });
    if (currentPlanIndex === 1) {
      plans.shift();
    }
    if (mode === 'trialStart') {
      return plans.map((plan) => ({
        ...plan,
        buttonText:
          plan.code !== 'unlimited' ? t('paywall.start_free_trial_button') : t('paywall.contact_sales_button'),
      }));
    }
    if (mode === 'trialEnd') {
      return plans.map((plan) => ({
        ...plan,
        showExtraMemberPrice: false,
        buttonText: plan.code !== 'unlimited' ? t('paywall.upgrade_button') : t('paywall.contact_sales_button'),
      }));
    }
    if (mode === 'upgrade') {
      const upgragePlans = [...plans];
      const currentPlanType = paywallState.currentPlanCode || subscriptionType + '-yearly';
      for (let i = 0; i < currentPlanIndex; i++) {
        upgragePlans[i].disabled = true;
      }
      if (isFreePlan || isAppsumo) {
        return upgragePlans.map((plan) => ({
          ...plan,
          buttonText: plan.code !== 'unlimited' ? t('paywall.upgrade_button') : t('paywall.contact_sales_button'),
          disabled: plan.code === mapToPlanCode(currentPlanType as BillingPlanCode) ? true : plan.disabled,
        }));
      } else {
        return upgragePlans.map((plan) => ({
          ...plan,
          selected: plan.code === paywallState.selectedPlan,
          selectable: true,
          disabled: plan.code === mapToPlanCode(currentPlanType as BillingPlanCode) ? true : plan.disabled,
          buttonText: plan.code === 'unlimited' ? t('paywall.contact_sales_button') : '',
        }));
      }
    }

    return plans;
  }, [
    paywallState.plans,
    paywallState.currentPlanCode,
    paywallState.selectedPlan,
    reason,
    mode,
    subscriptionType,
    isFreePlan,
    isAppsumo,
    t,
  ]);

  const handlePlanClick = useCallback(
    (plan: PaywallPlan) => {
      const code = plan.code === paywallState.selectedPlan ? null : plan.code;
      selectPlan(code);
    },
    [selectPlan, paywallState.selectedPlan]
  );

  return (
    <div className="paywall__plans flex gap-3 max-w-[100%]">
      {plansToShow.map((plan, i) => (
        <PaywallPlanColumn {...plan} key={i} onClick={handlePlanClick} />
      ))}
    </div>
  );
};
